import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';

export const SPOT_ATTRIBUTE = 'data-spot-id';

export const PICKED_CLASS = 'spot-picked';

export type DragSelect = {
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly claimClick: () => boolean;
};

type Sweep = { adding: boolean; touched: Set<string> };

function tileAt(target: Element | null): HTMLElement | null {
  return target === null ? null : target.closest<HTMLElement>(`[${SPOT_ATTRIBUTE}]`);
}

// Paints the tile itself: 160 React re-renders per sweep is what jank is made of.
function paint(tile: HTMLElement, adding: boolean): void {
  tile.classList.toggle(PICKED_CLASS, adding);
  tile.setAttribute('aria-pressed', String(adding));
}

// Press and sweep; the first tile decides whether it paints or erases. See §10.
export function useDragSelect({
  enabled,
  isPicked,
  onProgress,
  onCommit,
}: {
  enabled: boolean;
  isPicked: (spotId: string) => boolean;
  onProgress: (touched: ReadonlySet<string>, adding: boolean) => void;
  onCommit: (spotIds: readonly string[], adding: boolean) => void;
}): DragSelect {
  const sweep = useRef<Sweep | null>(null);
  const swallow = useRef(false);
  const latest = useRef({ isPicked, onProgress, onCommit });
  latest.current = { isPicked, onProgress, onCommit };
  const listening = useRef<(() => void) | null>(null);

  useEffect(() => () => listening.current?.(), []);

  const take = useCallback((tile: HTMLElement, current: Sweep): void => {
    const spotId = tile.getAttribute(SPOT_ATTRIBUTE) as string;
    if (current.touched.has(spotId)) return;
    current.touched.add(spotId);
    paint(tile, current.adding);
    latest.current.onProgress(current.touched, current.adding);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const tile = enabled ? tileAt(event.target as Element) : null;
      if (tile === null) return;
      // A finger that is painting must not also drag the map out from under it.
      event.preventDefault();
      const started: Sweep = {
        adding: !latest.current.isPicked(tile.getAttribute(SPOT_ATTRIBUTE) as string),
        touched: new Set(),
      };
      sweep.current = started;
      swallow.current = true;
      take(tile, started);

      const move = (moved: PointerEvent): void => {
        const over = tileAt(document.elementFromPoint(moved.clientX, moved.clientY));
        if (over !== null) take(over, started);
      };
      const end = (): void => {
        listening.current?.();
        sweep.current = null;
        latest.current.onCommit([...started.touched], started.adding);
      };
      listening.current = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        listening.current = null;
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
    },
    [enabled, take],
  );

  // The click that follows a sweep would undo the tile the sweep started on.
  const claimClick = useCallback((): boolean => {
    if (!swallow.current) return false;
    swallow.current = false;
    return true;
  }, []);

  return { onPointerDown, claimClick };
}
